/**
 * Computes a FEFO (first-expiry-first-out) stock-allocation suggestion for an
 * administered treatment and auto-populates the form's allocations field once
 * per unique product/clinic/quantity combination.
 *
 * Shared by the medication form (administered branch) and the consumable form,
 * which both dispense from real clinic stock. Returns the computed stock data
 * (FEFO allocations, available batches, stock status) for the caller to render.
 */
import { useEffect, useMemo, useRef } from 'react'

import type { ClinicId, ProductWithStock } from '@snootsvet/shared'

import type { EnrichedAllocationItem } from '@/frontend/features/orders/stock-allocation.utils'
import { computeStockAllocation } from '@/frontend/lib/stock'

interface UseFefoAllocationParams {
  /** The fetched product with its stock rows, or null/undefined while loading. */
  product: ProductWithStock | null | undefined
  /** Clinic the stock is dispensed from (a form field or a fixed context prop). */
  clinicId: ClinicId | null | undefined
  /** Currently-selected product id — part of the auto-populate reset key. */
  productId: string | null
  /** Requested quantity — part of the auto-populate reset key. */
  quantity: number | null
  /**
   * Whether the form opened with allocations already set (editing an existing
   * administered item) — suppresses the initial auto-populate so saved batches
   * aren't clobbered.
   */
  hasInitialAllocations: boolean
  /** Called with the FEFO suggestion when auto-populating. */
  onSuggest: (allocations: EnrichedAllocationItem[]) => void
}

export function useFefoAllocation({
  product,
  clinicId,
  productId,
  quantity,
  hasInitialAllocations,
  onSuggest,
}: UseFefoAllocationParams) {
  // Compute the FEFO suggestion synchronously from the fetched product's stock rows.
  const stockData = useMemo(() => {
    if (!product || !clinicId || !quantity) return null
    const rows = product.stock.filter((s) => s.clinicId === clinicId)
    return computeStockAllocation(rows, quantity)
  }, [product, clinicId, quantity])

  // Hold the latest callback in a ref so callers don't have to memoize it and
  // the populate effect can depend only on `stockData`.
  const onSuggestRef = useRef(onSuggest)
  onSuggestRef.current = onSuggest

  // Auto-populate allocations once per unique product/clinic/quantity combination.
  const hasPopulatedRef = useRef(hasInitialAllocations)
  const populateInputsRef = useRef({ productId, clinicId, quantity })
  if (
    productId !== populateInputsRef.current.productId ||
    clinicId !== populateInputsRef.current.clinicId ||
    quantity !== populateInputsRef.current.quantity
  ) {
    populateInputsRef.current = { productId, clinicId, quantity }
    hasPopulatedRef.current = false
  }

  useEffect(() => {
    if (stockData != null && stockData.allocations.length > 0 && !hasPopulatedRef.current) {
      hasPopulatedRef.current = true
      onSuggestRef.current(stockData.allocations)
    }
  }, [stockData])

  return stockData
}
